/**
 * Dropdown options for onboarding wizard
 * These must match the backend enums exactly
 */

import i18n from 'i18n';

// Localized via getters (D-getter): `value` is the backend enum and stays
// byte-for-byte; only the displayed `label` resolves in the active locale.
// Getters keep every export name and array shape identical for the wizard
// steps that import these, and the leading placeholder option (value '') is
// filtered out by WizardSelect — its label reuses the existing wizard keys.

export const ORG_SIZE_OPTIONS = [
  {
    value: '',
    get label() {
      return i18n.t('onboarding:wizard.selectSize', 'Select size');
    },
  },
  {
    value: '1-10',
    get label() {
      return i18n.t('onboarding:options.orgSize.s1to10', '1-10 employees');
    },
  },
  {
    value: '11-50',
    get label() {
      return i18n.t('onboarding:options.orgSize.s11to50', '11-50 employees');
    },
  },
  {
    value: '51-200',
    get label() {
      return i18n.t('onboarding:options.orgSize.s51to200', '51-200 employees');
    },
  },
  {
    value: '201-1000',
    get label() {
      return i18n.t('onboarding:options.orgSize.s201to1000', '201-1,000 employees');
    },
  },
  {
    value: '1000+',
    get label() {
      return i18n.t('onboarding:options.orgSize.s1000plus', '1,000+ employees');
    },
  },
];

export const INDUSTRY_OPTIONS = [
  {
    value: '',
    get label() {
      return i18n.t('onboarding:wizard.selectIndustry', 'Select industry');
    },
  },
  {
    value: 'Cement',
    get label() {
      return i18n.t('onboarding:options.industry.cement', 'Cement');
    },
  },
  {
    value: 'Steel & Iron',
    get label() {
      return i18n.t('onboarding:options.industry.steelIron', 'Steel & Iron');
    },
  },
  {
    value: 'Aluminum',
    get label() {
      return i18n.t('onboarding:options.industry.aluminum', 'Aluminum');
    },
  },
  {
    value: 'Fertilizers',
    get label() {
      return i18n.t('onboarding:options.industry.fertilizers', 'Fertilizers');
    },
  },
  {
    value: 'Electricity Generation',
    get label() {
      return i18n.t('onboarding:options.industry.electricity', 'Electricity Generation');
    },
  },
  {
    value: 'Hydrogen',
    get label() {
      return i18n.t('onboarding:options.industry.hydrogen', 'Hydrogen');
    },
  },
  {
    value: 'Chemicals',
    get label() {
      return i18n.t('onboarding:options.industry.chemicals', 'Chemicals');
    },
  },
  {
    value: 'General Manufacturing',
    get label() {
      return i18n.t('onboarding:options.industry.generalManufacturing', 'General Manufacturing');
    },
  },
  {
    value: 'Import/Export Trading',
    get label() {
      return i18n.t('onboarding:options.industry.importExport', 'Import/Export Trading');
    },
  },
  {
    value: 'Consulting Services',
    get label() {
      return i18n.t('onboarding:options.industry.consulting', 'Consulting Services');
    },
  },
  {
    value: 'Other',
    get label() {
      return i18n.t('onboarding:options.industry.other', 'Other');
    },
  },
];

export const USE_CASE_OPTIONS = [
  {
    value: '',
    get label() {
      return i18n.t('onboarding:wizard.selectUseCase', 'Select use case');
    },
  },
  {
    value: 'Regulatory Compliance',
    get label() {
      return i18n.t('onboarding:options.useCase.compliance', 'Regulatory Compliance');
    },
  },
  {
    value: 'Emissions Reporting',
    get label() {
      return i18n.t('onboarding:options.useCase.reporting', 'Emissions Reporting');
    },
  },
  {
    value: 'Emissions Tracking',
    get label() {
      return i18n.t('onboarding:options.useCase.tracking', 'Emissions Tracking');
    },
  },
  {
    value: 'Supplier Management',
    get label() {
      return i18n.t('onboarding:options.useCase.supplierManagement', 'Supplier Management');
    },
  },
  {
    value: 'Cost Optimization',
    get label() {
      return i18n.t('onboarding:options.useCase.costOptimization', 'Cost Optimization');
    },
  },
  {
    value: 'Other',
    get label() {
      return i18n.t('onboarding:options.useCase.other', 'Other');
    },
  },
];

export const TIMELINE_OPTIONS = [
  {
    value: '',
    get label() {
      return i18n.t('onboarding:wizard.selectTimeline', 'Select timeline');
    },
  },
  {
    value: 'Q4 2025',
    get label() {
      return i18n.t('onboarding:options.timeline.q4_2025', 'Q4 2025');
    },
  },
  {
    value: 'Q1 2026',
    get label() {
      return i18n.t('onboarding:options.timeline.q1_2026', 'Q1 2026');
    },
  },
  {
    value: 'Q2 2026',
    get label() {
      return i18n.t('onboarding:options.timeline.q2_2026', 'Q2 2026');
    },
  },
  {
    value: 'Q3 2026',
    get label() {
      return i18n.t('onboarding:options.timeline.q3_2026', 'Q3 2026');
    },
  },
  {
    value: 'Q4 2026',
    get label() {
      return i18n.t('onboarding:options.timeline.q4_2026', 'Q4 2026');
    },
  },
  {
    value: 'Q1 2027',
    get label() {
      return i18n.t('onboarding:options.timeline.q1_2027', 'Q1 2027');
    },
  },
  {
    value: 'Later',
    get label() {
      return i18n.t('onboarding:options.timeline.later', 'Later');
    },
  },
  {
    value: 'Not Sure Yet',
    get label() {
      return i18n.t('onboarding:options.timeline.notSure', 'Not Sure Yet');
    },
  },
];

export const VOLUME_OPTIONS = [
  {
    value: '',
    get label() {
      return i18n.t('onboarding:wizard.selectVolume', 'Select volume');
    },
  },
  {
    value: 'Less than 50 tonnes/year',
    get label() {
      return i18n.t('onboarding:options.volume.lessThan50', 'Less than 50 tonnes/year');
    },
  },
  {
    value: '50-100 tonnes/year',
    get label() {
      return i18n.t('onboarding:options.volume.v50to100', '50-100 tonnes/year');
    },
  },
  {
    value: '100-1,000 tonnes/year',
    get label() {
      return i18n.t('onboarding:options.volume.v100to1000', '100-1,000 tonnes/year');
    },
  },
  {
    value: '1,000-10,000 tonnes/year',
    get label() {
      return i18n.t('onboarding:options.volume.v1000to10000', '1,000-10,000 tonnes/year');
    },
  },
  {
    value: '10,000-100,000 tonnes/year',
    get label() {
      return i18n.t('onboarding:options.volume.v10000to100000', '10,000-100,000 tonnes/year');
    },
  },
  {
    value: '100,000+ tonnes/year',
    get label() {
      return i18n.t('onboarding:options.volume.v100000plus', '100,000+ tonnes/year');
    },
  },
  {
    value: 'Not Applicable',
    get label() {
      return i18n.t('onboarding:options.volume.notApplicable', 'Not Applicable');
    },
  },
];

export const JOB_ROLE_OPTIONS = [
  {
    value: '',
    get label() {
      return i18n.t('onboarding:wizard.selectRole', 'Select your role');
    },
  },
  {
    value: 'CEO',
    get label() {
      return i18n.t('onboarding:options.role.ceo', 'CEO');
    },
  },
  {
    value: 'CTO',
    get label() {
      return i18n.t('onboarding:options.role.cto', 'CTO');
    },
  },
  {
    value: 'CFO',
    get label() {
      return i18n.t('onboarding:options.role.cfo', 'CFO');
    },
  },
  {
    value: 'Head of Procurement',
    get label() {
      return i18n.t('onboarding:options.role.headProcurement', 'Head of Procurement');
    },
  },
  {
    value: 'Head of Sustainability',
    get label() {
      return i18n.t('onboarding:options.role.headSustainability', 'Head of Sustainability');
    },
  },
  {
    value: 'Operations Manager',
    get label() {
      return i18n.t('onboarding:options.role.opsManager', 'Operations Manager');
    },
  },
  {
    value: 'Compliance Officer',
    get label() {
      return i18n.t('onboarding:options.role.complianceOfficer', 'Compliance Officer');
    },
  },
  {
    value: 'IT Manager',
    get label() {
      return i18n.t('onboarding:options.role.itManager', 'IT Manager');
    },
  },
  {
    value: 'Finance Manager',
    get label() {
      return i18n.t('onboarding:options.role.financeManager', 'Finance Manager');
    },
  },
  {
    value: 'Other',
    get label() {
      return i18n.t('onboarding:options.role.other', 'Other');
    },
  },
];

export const HEAR_ABOUT_OPTIONS = [
  {
    value: '',
    get label() {
      return i18n.t('onboarding:wizard.selectSource', 'Select source');
    },
  },
  {
    value: 'Search Engine (Google, Bing, etc.)',
    get label() {
      return i18n.t('onboarding:options.source.searchEngine', 'Search Engine (Google, Bing, etc.)');
    },
  },
  {
    value: 'Social Media',
    get label() {
      return i18n.t('onboarding:options.source.socialMedia', 'Social Media');
    },
  },
  {
    value: 'LinkedIn',
    get label() {
      return i18n.t('onboarding:options.source.linkedin', 'LinkedIn');
    },
  },
  {
    value: 'Referral from colleague',
    get label() {
      return i18n.t('onboarding:options.source.referral', 'Referral from colleague');
    },
  },
  {
    value: 'Industry Event/Conference',
    get label() {
      return i18n.t('onboarding:options.source.event', 'Industry Event/Conference');
    },
  },
  {
    value: 'Email Campaign',
    get label() {
      return i18n.t('onboarding:options.source.emailCampaign', 'Email Campaign');
    },
  },
  {
    value: 'Consultant Recommendation',
    get label() {
      return i18n.t('onboarding:options.source.consultant', 'Consultant Recommendation');
    },
  },
  {
    value: 'Trade Publication',
    get label() {
      return i18n.t('onboarding:options.source.tradePublication', 'Trade Publication');
    },
  },
  {
    value: 'Other',
    get label() {
      return i18n.t('onboarding:options.source.other', 'Other');
    },
  },
];

export const USED_SOFTWARE_OPTIONS = [
  {
    value: '',
    get label() {
      return i18n.t('onboarding:wizard.select', 'Select');
    },
  },
  {
    value: 'Yes',
    get label() {
      return i18n.t('common:actions.yes', 'Yes');
    },
  },
  {
    value: 'No',
    get label() {
      return i18n.t('common:actions.no', 'No');
    },
  },
];

export const CALENDLY_ONBOARDING_URL =
  'https://calendly.com/victor-e-0/onboarding-session?hide_gdpr_banner=1';

// Same Calendly event TrialExpiredPage/PaymentPage already open for a sales
// call with Victor (CEO) — hoisted here so new call sites (e.g. the free
// supplier Upgrade page) reuse one constant instead of pasting the URL again.
export const CALENDLY_PRODUCT_DEMO_URL = 'https://calendly.com/victor-e-0/product-demo';
